import {
  BrandTemplateDataSet,
  DesignAutofillData,
} from '../../../providers/api/dtos';
import { isEmpty } from '../../../utils/object/object';
import {
  BRAND_TEMPLATE_AUTOFILL_ENTITY_SOURCES_BY_LENGTH,
  BRAND_TEMPLATE_AUTOFILL_TRANSFORMER_REGISTRY,
} from './constants';
import {
  BrandTemplateAutofillDecodedDataset,
  BrandTemplateAutofillDecodedVariable,
  BrandTemplateAutofillDecodeResult,
  BrandTemplateAutofillEntitySource,
  BrandTemplateAutofillEntitySources,
  BrandTemplateAutofillLooseTransformer,
  BrandTemplateAutofillResolvedField,
  BrandTemplateAutofillSegment,
} from './types';

const BRAND_TEMPLATE_AUTOFILL_SEGMENT_RE = /^([a-zA-Z]+)(?:\((\d+)\))?$/;
const BRAND_TEMPLATE_AUTOFILL_ENTITY_INDEX_RE = /^\((\d+)\)/;

const parsePath = (rest: string): BrandTemplateAutofillSegment[] | string => {
  const tokens = rest.split('_');
  const invalidToken = tokens.find((token) => !BRAND_TEMPLATE_AUTOFILL_SEGMENT_RE.test(token));

  if (invalidToken !== undefined) return invalidToken;

  return tokens.map((token) => {
    const match = token.match(BRAND_TEMPLATE_AUTOFILL_SEGMENT_RE)!;

    return {
      name: match[1],
      ...(match[2] !== undefined && { index: parseInt(match[2], 10) }),
    };
  });
};

const renderSegment = (name: string, index: number | undefined, concrete: boolean): string => {
  if (index === undefined) return name;

  return concrete ? `${name}(${index})` : `${name}($POSITION)`;
};

const buildRegistryKey = (
  entity: string,
  entityIndex: number | undefined,
  path: BrandTemplateAutofillSegment[],
  concrete: boolean,
): string => {
  const entityPart = renderSegment(entity, entityIndex, concrete);
  const pathPart = path.map((s) => renderSegment(s.name, s.index, concrete)).join('_');

  return `${entityPart}_${pathPart}`;
};

const lookupRegistryTransformer = (
  exactKey: string,
  patternKey: string,
): BrandTemplateAutofillLooseTransformer | undefined => {
  const registry = BRAND_TEMPLATE_AUTOFILL_TRANSFORMER_REGISTRY as Record<string, BrandTemplateAutofillLooseTransformer | undefined>;

  return registry[exactKey] ?? registry[patternKey];
};

const extractAllPositions = (
  entityIndex: number | undefined,
  path: BrandTemplateAutofillSegment[],
): number[] => {
  const pathPositions = path.flatMap((s) => (s.index !== undefined ? [s.index - 1] : []));

  return entityIndex !== undefined
    ? [entityIndex - 1, ...pathPositions]
    : pathPositions;
};

const findEntitySource = (raw: string): BrandTemplateAutofillEntitySource | undefined => BRAND_TEMPLATE_AUTOFILL_ENTITY_SOURCES_BY_LENGTH.find((e) => {
  if (!raw.startsWith(e)) return false;

  const next = raw.charAt(e.length);

  return next === '_' || next === '(';
});

/**
 * Decodes a raw Canva autofill variable string into a structured shape.
 *
 * Never throws: every outcome is expressed as a `BrandTemplateAutofillDecodeResult`,
 * with structured failure causes left for the caller to interpret.
 */
export const decodeBrandTemplateAutofillVariable = (
  raw: string,
): BrandTemplateAutofillDecodeResult => {
  const entity = findEntitySource(raw);
  if (!entity) {
    return {
      ok: false,
      raw,
      failure: { code: 'unknown_entity' },
    };
  }

  let rest = raw.slice(entity.length);
  let entityIndex: number | undefined;

  const entityIndexMatch = rest.match(BRAND_TEMPLATE_AUTOFILL_ENTITY_INDEX_RE);

  if (entityIndexMatch) {
    entityIndex = parseInt(entityIndexMatch[1], 10);
    rest = rest.slice(entityIndexMatch[0].length);
  }

  if (!rest.startsWith('_')) {
    return {
      ok: false,
      raw,
      failure: { code: 'invalid_variable', entity, detail: `expected "_" after "${entity}"` },
    };
  }

  const path = parsePath(rest.slice(1));
  if (typeof path === 'string') {
    return {
      ok: false,
      raw,
      failure: { code: 'invalid_variable', entity, detail: `invalid segment "${path}"` },
    };
  }

  const positions = extractAllPositions(entityIndex, path);
  const exactKey = buildRegistryKey(entity, entityIndex, path, true);
  const patternKey = buildRegistryKey(entity, entityIndex, path, false);

  const registryTransformer = lookupRegistryTransformer(exactKey, patternKey);
  if (!registryTransformer) {
    return {
      ok: false,
      raw,
      failure: { code: 'no_transformer', entity },
    };
  }

  return {
    ok: true,
    variable: {
      raw,
      entity,
      entityIndex,
      path,
      resolve: (source) => registryTransformer(source[entity], { positions }),
    },
  };
};

/**
 * Decodes all keys in a `BrandTemplateDataSet`, partitioning them into
 * successfully decoded `variables` and `failures` with structured causes.
 */
const HIGH_SEVERITY_CODES = new Set([
  'invalid_variable',
  'unknown_entity',
]);
export const decodeBrandTemplateDataset = (
  dataset: BrandTemplateDataSet,
): BrandTemplateAutofillDecodedDataset => Object.keys(dataset).reduce<BrandTemplateAutofillDecodedDataset>(
  (acc, raw) => {
    const result = decodeBrandTemplateAutofillVariable(raw);

    if (result.ok) {
      return {
        ...acc,
        variables: [...acc.variables, result.variable],
      };
    }

    const severity = acc.severity === 'high'
      || HIGH_SEVERITY_CODES.has(result.failure.code)
      ? 'high'
      : 'medium';

    return {
      severity,
      variables: acc.variables,
      failures: [...acc.failures, result],
    };
  },
  {
    severity: 'none',
    variables: [],
    failures: [],
  },
);

/** Resolves decoded variables against the provided source, omitting undefined results. */
export const buildAutofillData = async (
  variables: BrandTemplateAutofillDecodedVariable[],
  source: BrandTemplateAutofillEntitySources,
): Promise<DesignAutofillData | undefined> => {
  const entries = await Promise.all(
    variables.map(async (variable) => {
      const resolved = await variable.resolve(source);

      return [variable.raw, resolved] as const;
    }),
  );

  const autofillData = Object.fromEntries(
    entries.filter((entry): entry is readonly [string, BrandTemplateAutofillResolvedField] => entry[1] !== undefined),
  );

  return isEmpty(autofillData) ? undefined : autofillData;
};

/** Returns the set of entity sources referenced by the decoded variables. */
export const getReferencedEntitySources = (
  variables: BrandTemplateAutofillDecodedVariable[],
): Set<BrandTemplateAutofillEntitySource> => new Set(variables.map((v) => v.entity));

const extractUniqueIndices = (values: (number | undefined)[]): number[] => [
  ...new Set(values.filter((v): v is number => v !== undefined)),
].sort((a, b) => a - b);

/**
 * Returns the 1-based entity slot indices for a list entity.
 * @example getEntitySlots(variables, 'sold_properties') // → [1, 2]
 */
export const getEntitySlots = (
  variables: BrandTemplateAutofillDecodedVariable[],
  entity: BrandTemplateAutofillEntitySource,
): number[] => extractUniqueIndices(
  variables
    .filter((v) => v.entity === entity)
    .map((v) => v.entityIndex),
);

type EntityQuery = BrandTemplateAutofillEntitySource | { entity: BrandTemplateAutofillEntitySource; index?: number };

type FieldQuery = string | { field: string; index?: number };

const resolveEntityQuery = (
  query: EntityQuery,
): { entity: BrandTemplateAutofillEntitySource; index?: number } => (
  typeof query === 'string' ? { entity: query, index: undefined } : query
);

const resolveFieldQuery = (query: FieldQuery): { field: string; index?: number } => (
  typeof query === 'string' ? { field: query, index: undefined } : query
);

const matchesFieldQuery = (
  segment: BrandTemplateAutofillSegment | undefined,
  query: FieldQuery,
): boolean => {
  if (!segment) return false;

  const { field, index } = resolveFieldQuery(query);

  return segment.name === field && (index === undefined || segment.index === index);
};

/**
 * Returns the 1-based slot indices for the final field in a path chain,
 * optionally scoped to a specific entity slot and/or specific parent segment indices.
 * @example getFieldSlots(variables, 'property_data', 'photos') // → [1, 2]
 * @example getFieldSlots(variables, { entity: 'sold_properties', index: 1 }, 'photos') // → [1]
 * @example getFieldSlots(variables, 'agent_data', { field: 'propertiesSold', index: 1 }, 'photos') // → [1]
 */
export const getFieldSlots = (
  variables: BrandTemplateAutofillDecodedVariable[],
  entityQuery: EntityQuery,
  ...fields: FieldQuery[]
): number[] => {
  const { entity, index: entityIndex } = resolveEntityQuery(entityQuery);
  const lastIdx = fields.length - 1;

  return extractUniqueIndices(
    variables
      .filter((v) => v.entity === entity
        && (entityIndex === undefined || v.entityIndex === entityIndex)
        && fields.every((f, i) => matchesFieldQuery(v.path[i], f)))
      .map((v) => v.path[lastIdx]?.index),
  );
};
